// axios 请求库二次封装
import axios from 'axios';
import {useAuthStore} from "@/store";
import {message, notification} from "ant-design-vue";
import router from "@/router";
import {t} from "@/utils/i18n";

// 创建请求实例
const instance = axios.create({
    baseURL: process.env.VUE_APP_URL,
    // baseURL: '/',
    // 指定请求超时的毫秒数
    timeout: 1000 * 60 * 5,
    // 表示跨域请求时是否需要使用凭证
    withCredentials: true,
    crossDomain: true,
    // 是否允许重复请求
    repeatRequest: false,
});

const getCancelToken = () => {
    let cancel;
    const cancelToken = new axios.CancelToken(c => {
        cancel = c;
    });
    return { cancel, cancelToken };
}

const pendingRequestsMap = new Map();

// 前置拦截器（发起请求之前的拦截）
instance.interceptors.request.use(
    (config) => {
        const authStore = useAuthStore();
        const token = localStorage.getItem('storm_token');
        config.headers['Authorization'] = token ? `Bearer ${token}` : 'Bearer null';
        // 设置特定接口的超时时间（10分钟）
        if (config.url?.includes('/v3/services/mfm/api/upload-controller/firmware-file-upload')) {
            config.timeout = 1000 * 60 * 10;
        }
        if(config.url !== '/v3/services/mfm/api/upload-controller/upload' && config.url.indexOf('/v3/services/mrm/api/alertLogInit') < 0) {
            // 方法一：通过axios自带的CancelToken方法进行拦截重复请求
            // 生成当前请求的key
            const key = config.url + JSON.stringify(config.params) + JSON.stringify(config.data);
            // 如果这个请求已经在发送了，则取消之前的请求
            if (pendingRequestsMap.has(key)) {
                pendingRequestsMap.get(key).cancel();
            }
            // 创建新的CancelToken
            const { cancelToken, cancel } = getCancelToken();
            // 存储请求的key和取消函数
            pendingRequestsMap.set(key, { cancelToken, cancel });
            // 将CancelToken添加到请求配置中
            config.cancelToken = cancelToken;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// 后置拦截器（获取到响应时的拦截）
// let  is401 = false;
instance.interceptors.response.use(
    (response) => {
        /**
         * 根据你的项目实际情况来对 response 和 error 做处理
         * 这里对 response 和 error 不做任何处理，直接返回
         */

        // 请求完成后，从 pendingRequestsMap 中删除该请求
        const config = response.config;
        if(config.url !== '/v3/services/mfm/api/upload-controller/upload' && config.url.indexOf('/v3/services/mrm/api/alertLogInit') < 0) {
            const key = config.url + JSON.stringify(config.params) + JSON.stringify(config.data);
            pendingRequestsMap.delete(key);
        }

        return response.data;
    },
    (error) => {
        const { response } = error;
        const authStore = useAuthStore();

        // 请求失败或被取消时，从 pendingRequestsMap 中删除该请求
        const config = error.config;
        if(config && config.url !== '/v3/services/mfm/api/upload-controller/upload' && config.url.indexOf('/v3/services/mrm/api/alertLogInit') < 0) {
            const key = config.url + JSON.stringify(config.params) + JSON.stringify(config.data);
            pendingRequestsMap.delete(key);
        }

        if (response && response.status == 401) {
            // 由于登录页登录失败返回也是401，不需要刷新或者跳转，所以这里直接返回，不刷新页面
            // if(!is401) {、
            // 对于登录相关API的401错误，只拒绝Promise而不执行其他操作
            if (response.config.url.includes('/v3/api/login') &&
                !response.config.url.includes('v3/api/loginlogoutaspartner')) {
                return Promise.reject(response.data);
            } else {
                authStore.logout();
                message.destroy();
                if (response.data != null && response.data.msg) {
                    message.error(t(response.data.msg));
                } else if (response.data != null && response.data.detail === 'Bad credentials') {
                    message.error(t('t-invalid-credentials'))
                } else {
                    message.error(t('t-login-status-expired'));
                }
                location.reload();
            }
            // }
        } else {
            if(response && response.config.url.includes('/verificationToken')) {
                authStore.logout();
                location.reload();
            }
            if(response?.data) return Promise.reject(response.data);
        }
    },
);

// 导出常用函数
/**
 * @param {string} url
 * @param {object} data
 * @param {object} params
 */
export const post = (url, data = {}, params = {}) => {
    return instance({
        method: 'post',
        url,
        data,
        params,
    });
};
/**
 * @param {string} url
 * @param {object} formData
 */
export const post2 = (url, formData) => {
    return instance({
        method: 'post',
        url,
        data: formData,
    });
};
/**
 * @param {string} url
 * @param {object} formData
 */
export const uploadProgress = (url, formData, progress) => {
    return instance({
        method: 'post',
        url,
        data: formData,
        onUploadProgress: progress
    });
};

/**
 * @param {string} url
 * @param {object} params
 */
export const get = (url, params = {}) => {
    return instance({
        method: 'get',
        url,
        params,
    });
};

/**
 * @param {string} url
 * @param {object} data
 * @param {object} params
 */
export const put = (url, data = {}, params = {}) => {
    return instance({
        method: 'put',
        url,
        params,
        data,
    });
};
/**
 * @param {string} url
 * @param {FormData} formData
 */
export const put2 = async (url, formData) => {
    return instance({
        method: 'put',
        url,
        data: formData,
    });
};

/**
 * @param {string} url
 * @param {object} params
 */
export const _delete = (url, params = {}) => {
    return instance({
        method: 'delete',
        url,
        params,
    });
};

/**
 * @param {string} url
 * @param {object} params
 */
export const download = (url, options = {}) => {
    return instance({
        method: 'get', // 使用GET方法通常用于下载资源
        url,
        ...options, // 允许传入额外的axios配置项（如headers）

        // 下载文件时，需要设置响应类型为blob，以便浏览器能够识别并处理为文件下载
        responseType: 'blob',

        // 可选：设置请求头中的Accept属性，具体取决于您要下载的文件类型
        headers: {
            ...options.headers, // 保留用户可能传递的现有headers
            // Accept: 'application/octet-stream', // 示例：接受任意二进制流数据
            // 如果您知道确切的文件类型，可替换为相应的MIME类型，例如：
            // Accept: 'application/pdf' for PDF files,
            // Accept: 'image/png' for PNG images, etc.
        },
    });
};
export const download2 = (url, options = {}) => {
    return instance({
        method: 'post', // 使用GET方法通常用于下载资源
        url,
        ...options, // 允许传入额外的axios配置项（如headers）

        // 下载文件时，需要设置响应类型为blob，以便浏览器能够识别并处理为文件下载
        responseType: 'blob',

        // 可选：设置请求头中的Accept属性，具体取决于您要下载的文件类型
        headers: {
            ...options.headers, // 保留用户可能传递的现有headers
        },
    });
};

export default instance;


